import React, { useState, useCallback, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import {
    intl,
    DmsModal,
    DmsButton,
    fetchData,
    HTTP_STATUS,
} from "dmsbasereact";
import {
    Box,
    Grid,
    Typography,
    Card,
    CardContent,
    IconButton,
    FormControlLabel,
    Checkbox,
    TextField,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import Swal from "sweetalert2";
import { DmsAutocomplete } from "../DmsAutoComplete/DmsAutoComplete";
import { formatCnpj } from "../../utils/formatters";
import { getInstitutionCategoryLabel } from "../../constants/InstitutionType";

/**
 * Extracts the CNPJ root (first 8 digits) used to identify the company.
 * Branches share the same root but differ in the 4-digit branch number.
 * @param {string} cnpj - Raw CNPJ digits (14 chars)
 * @returns {string} First 8 digits
 */
const getCnpjRoot = (cnpj) => {
    if (!cnpj) return "";
    const digits = String(cnpj).replace(/\D/g, "");
    return digits.substring(0, 8);
};

/**
 * PartnerBranchLinkModal - Modal for adding economic group or branch institutions.
 * Flow: Select a parent (Matriz) by CNPJ -> See branches with same CNPJ root -> Select branches.
 */
export const PartnerBranchLinkModal = ({
    isVisible,
    onClose,
    onSave,
    fixedMatrizPk = null,
    fixedMatrizCnpj = "",
    fixedMatrizName = "",
    fixedIsPublic = false,
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    const isMatrizLocked = !!fixedMatrizPk;

    const [isPublic, setIsPublic] = useState(fixedIsPublic);
    const [allInstitutions, setAllInstitutions] = useState([]);
    const [selectedMatrizPk, setSelectedMatrizPk] = useState(null);
    const [selectedBranchPks, setSelectedBranchPks] = useState([]);
    const [linkedBranchPks, setLinkedBranchPks] = useState([]);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (isVisible && isMatrizLocked) {
            setIsPublic(fixedIsPublic);
        }
    }, [isVisible, isMatrizLocked, fixedIsPublic]);

    useEffect(() => {
        const fetchAllInstitutions = async () => {
            try {
                const response = await fetchData("/institution/all/approved", "GET", true);
                const data = response.data.data.rows || response.data.data;
                if (Array.isArray(data)) {
                    setAllInstitutions(
                        data.map((item) => ({
                            value: item.organization_branch_pk,
                            cnpj: item.organization_branch?.cnpj || "",
                            name: item.organization_branch?.name || item.name || "",
                            is_public: !!item.is_public,
                            category: item.organization_branch?.category || null,
                        }))
                    );
                }
            } catch (error) {
                console.error("Error fetching institutions:", error);
            }
        };

        if (isVisible) {
            fetchAllInstitutions();
            if (fixedMatrizPk) {
                setSelectedMatrizPk(fixedMatrizPk);
            }
        }
    }, [isVisible, fixedMatrizPk]);

    // Fetch already-linked branches when matriz changes
    useEffect(() => {
        const fetchLinkedBranches = async () => {
            if (!selectedMatrizPk) {
                setLinkedBranchPks([]);
                return;
            }
            try {
                const response = await fetchData(
                    `/partner/branch/${selectedMatrizPk}/branches`,
                    "GET",
                    true
                );
                const data = response?.data?.data || [];
                if (Array.isArray(data)) {
                    setLinkedBranchPks(data.map((item) => item.organization_branch_pk));
                }
            } catch {
                setLinkedBranchPks([]);
            }
        };

        if (isVisible && selectedMatrizPk) {
            fetchLinkedBranches();
        }
    }, [isVisible, selectedMatrizPk]);

    const matrizOptions = useMemo(
        () =>
            allInstitutions.map((inst) => ({
                label: isPublic ? inst.name : formatCnpj(inst.cnpj),
                value: inst.value,
                name: inst.name,
                cnpj: inst.cnpj,
            })),
        [allInstitutions, isPublic]
    );

    const selectedMatriz = matrizOptions.find((opt) => opt.value === selectedMatrizPk);
    const matrizCnpjRoot = selectedMatriz
        ? getCnpjRoot(allInstitutions.find((i) => i.value === selectedMatriz.value)?.cnpj)
        : "";

    const branchOptions = useMemo(() => {
        if (!selectedMatrizPk) return [];

        if (isPublic) {
            return allInstitutions
                .filter((inst) =>
                    inst.is_public
                    && inst.value !== selectedMatrizPk
                    && !linkedBranchPks.includes(inst.value)
                )
                .map((inst) => ({
                    label: inst.name,
                    value: inst.value,
                    name: inst.name,
                    cnpj: inst.cnpj,
                    category: inst.category,
                }));
        }

        if (!matrizCnpjRoot) return [];
        return allInstitutions
            .filter((inst) =>
                getCnpjRoot(inst.cnpj) === matrizCnpjRoot
                && inst.value !== selectedMatrizPk
                && !linkedBranchPks.includes(inst.value)
            )
            .map((inst) => ({
                label: formatCnpj(inst.cnpj),
                value: inst.value,
                name: inst.name,
                cnpj: inst.cnpj,
                category: inst.category,
            }));
    }, [allInstitutions, matrizCnpjRoot, selectedMatrizPk, isPublic, linkedBranchPks]);

    const selectedBranches = branchOptions.filter((opt) => selectedBranchPks.includes(opt.value));

    const handleMatrizChange = useCallback((value) => {
        const pk = Array.isArray(value) ? value[0] ?? null : value;
        setSelectedMatrizPk(pk);
        setSelectedBranchPks([]);
    }, []);

    const handlePublicChange = useCallback((e) => {
        setIsPublic(e.target.checked);
        setSelectedMatrizPk(null);
        setSelectedBranchPks([]);
    }, []);

    const handleRemoveBranch = useCallback((valueToRemove) => {
        setSelectedBranchPks((prev) => prev.filter((v) => v !== valueToRemove));
    }, []);

    const handleClose = useCallback(() => {
        setIsPublic(fixedIsPublic);
        setSelectedMatrizPk(fixedMatrizPk || null);
        setSelectedBranchPks([]);
        setLinkedBranchPks([]);
        onClose();
    }, [onClose, fixedMatrizPk, fixedIsPublic]);

    const handleSave = useCallback(async () => {
        if (!selectedMatrizPk) {
            Swal.fire({
                icon: "warning",
                title: intl.get("MissingInformation").d("Missing Information"),
                text: intl.get("SelectParentInstitution").d("Please select the parent institution (Matriz)."),
            });
            return;
        }

        if (selectedBranchPks.length === 0) {
            Swal.fire({
                icon: "warning",
                title: intl.get("MissingInformation").d("Missing Information"),
                text: intl.get("SelectAtLeastOneBranch").d("Please select at least one branch."),
            });
            return;
        }

        setSaving(true);
        try {
            const payload = {
                is_public: isPublic,
                matriz_pk: selectedMatrizPk,
                branch_pks: selectedBranchPks,
            };

            const response = await fetchData("/partner/branch/link", "POST", true, payload);

            if (response.status === HTTP_STATUS.CREATED_201 || response.status === HTTP_STATUS.OK_200) {
                Swal.fire({
                    icon: "success",
                    title: intl.get("Success").d("Success"),
                    text: intl.get("PartnerBranchCreatedSuccessfully").d("Branch link created successfully."),
                });
                if (onSave) onSave();
                handleClose();
            } else {
                Swal.fire({
                    icon: "error",
                    title: intl.get("Error").d("Error"),
                    text: response.data?.detail || intl.get("FailedToCreatePartnerBranch").d("Failed to create branch link."),
                });
            }
        } catch (error) {
            console.error("Error creating partner branch link:", error);
            Swal.fire({
                icon: "error",
                title: intl.get("Error").d("Error"),
                text: intl.get("FailedToCreatePartnerBranch").d("Failed to create branch link."),
            });
        } finally {
            setSaving(false);
        }
    }, [isPublic, selectedMatrizPk, selectedBranchPks, onSave, handleClose]);

    return (
        <DmsModal
            isVisible={isVisible}
            onClose={handleClose}
            fullScreen={isMobile}
            maxWidth="sm"
            fullWidth
        >
            <DmsModal.Header
                title={intl.get("AddEconomicGroupOrBranch").d("Add Economic Group or Branch")}
                onClose={handleClose}
            />
            <DmsModal.Body>
                <Box sx={{ p: isMobile ? 1 : 2 }}>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={isPublic}
                                        onChange={handlePublicChange}
                                        disabled={isMatrizLocked}
                                    />
                                }
                                label={intl.get("IsPublicInstitution").d("Is Public Institution")}
                            />
                        </Grid>

                        {/* Step 1: Select Matriz */}
                        <Grid item xs={12}>
                            <Typography variant="body2" sx={{ fontWeight: "bold", mb: 1 }}>
                                {isPublic
                                    ? intl.get("ParentInstitution").d("Parent Institution (Matriz)")
                                    : intl.get("ParentCNPJ").d("Parent CNPJ (Matriz)")}
                            </Typography>
                            {isMatrizLocked ? (
                                <TextField
                                    fullWidth
                                    variant="outlined"
                                    disabled
                                    value={fixedMatrizCnpj ? `${fixedMatrizName} - ${formatCnpj(fixedMatrizCnpj)}` : fixedMatrizName}
                                />
                            ) : (
                                <>
                                    <DmsAutocomplete
                                        label={isPublic
                                            ? intl.get("SearchByName").d("Search by name")
                                            : intl.get("SearchByCNPJ").d("Search by CNPJ")}
                                        name="matriz_pk"
                                        multiple={false}
                                        options={matrizOptions}
                                        value={selectedMatrizPk}
                                        onChange={handleMatrizChange}
                                    />
                                    {selectedMatriz && !isPublic && (
                                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
                                            {selectedMatriz.name}
                                        </Typography>
                                    )}
                                    {selectedMatriz && isPublic && (
                                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
                                            {selectedMatriz.cnpj ? formatCnpj(selectedMatriz.cnpj) : ""}
                                        </Typography>
                                    )}
                                </>
                            )}
                        </Grid>

                        {/* Step 2: Select Branches */}
                        {selectedMatrizPk && (
                            <Grid item xs={12}>
                                <Typography variant="body2" sx={{ fontWeight: "bold", mb: 1 }}>
                                    {intl.get("Branches").d("Branches")}
                                </Typography>
                                {branchOptions.length === 0 ? (
                                    <Typography variant="body2" color="text.secondary">
                                        {isPublic
                                            ? intl.get("NoPublicInstitutionsFound").d("No public institutions available.")
                                            : intl.get("NoBranchesFound").d("No branches found for this CNPJ root.")}
                                    </Typography>
                                ) : (
                                    <>
                                        <DmsAutocomplete
                                            key={`branch-select-${selectedMatrizPk}-${selectedBranchPks.join(",")}`}
                                            label={isPublic
                                                ? intl.get("SelectBranchesByName").d("Select branches by name")
                                                : intl.get("SelectBranchesByCNPJ").d("Select branches by CNPJ")}
                                            name="branch_pks"
                                            multiple={true}
                                            options={branchOptions}
                                            value={selectedBranchPks}
                                            onChange={setSelectedBranchPks}
                                        />
                                        <Box sx={{ mt: 1, display: "flex", justifyContent: "flex-end" }}>
                                            <DmsButton
                                                variant="secondary"
                                                text={intl.get("SelectAll").d("Select All")}
                                                onClick={() => setSelectedBranchPks(branchOptions.map((opt) => opt.value))}
                                                disabled={selectedBranchPks.length === branchOptions.length}
                                                size="small"
                                            />
                                        </Box>
                                        {selectedBranches.length > 0 && (
                                            <Box
                                                sx={{
                                                    mt: 1,
                                                    maxHeight: 200,
                                                    overflowY: "auto",
                                                    border: "1px solid",
                                                    borderColor: "grey.300",
                                                    borderRadius: 1,
                                                    p: 1,
                                                }}
                                            >
                                                <Grid container spacing={2}>
                                                    {selectedBranches.map((branch) => (
                                                        <Grid item xs={12} sm={6} key={branch.value}>
                                                            <Card
                                                                variant="outlined"
                                                                sx={{ backgroundColor: "grey.100" }}
                                                            >
                                                                <CardContent sx={{ py: 1.5, px: 2, "&:last-child": { pb: 1.5 } }}>
                                                                    <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
                                                                        <Box sx={{ pr: 1, flex: 1 }}>
                                                                            <Typography
                                                                                variant="body2"
                                                                                sx={{ fontWeight: "bold", wordBreak: "break-word" }}
                                                                            >
                                                                                {branch.name}
                                                                            </Typography>
                                                                            {branch.cnpj && (
                                                                                <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                                                                                    {formatCnpj(branch.cnpj)}
                                                                                </Typography>
                                                                            )}
                                                                            {branch.category && (
                                                                                <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                                                                                    {getInstitutionCategoryLabel(branch.category)}
                                                                                </Typography>
                                                                            )}
                                                                        </Box>
                                                                        <IconButton
                                                                            size="small"
                                                                            onClick={() => handleRemoveBranch(branch.value)}
                                                                            sx={{ flexShrink: 0, mt: -0.5 }}
                                                                        >
                                                                            <CloseIcon fontSize="small" />
                                                                        </IconButton>
                                                                    </Box>
                                                                </CardContent>
                                                            </Card>
                                                        </Grid>
                                                    ))}
                                                </Grid>
                                            </Box>
                                        )}
                                    </>
                                )}
                            </Grid>
                        )}
                    </Grid>
                </Box>
            </DmsModal.Body>
            <DmsModal.Footer>
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: isMobile ? "column" : "row",
                        justifyContent: "flex-end",
                        gap: 1,
                        width: "100%",
                        p: isMobile ? 1 : 2,
                    }}
                >
                    <DmsButton
                        variant="secondary"
                        text={intl.get("Cancel").d("Cancel")}
                        onClick={handleClose}
                        fullWidth={isMobile}
                        disabled={saving}
                    />
                    <DmsButton
                        variant="primary"
                        text={saving ? intl.get("Saving").d("Saving...") : intl.get("Save").d("Save")}
                        onClick={handleSave}
                        fullWidth={isMobile}
                        disabled={saving}
                    />
                </Box>
            </DmsModal.Footer>
        </DmsModal>
    );
};

PartnerBranchLinkModal.propTypes = {
    isVisible: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onSave: PropTypes.func,
    fixedMatrizPk: PropTypes.number,
    fixedMatrizCnpj: PropTypes.string,
    fixedMatrizName: PropTypes.string,
    fixedIsPublic: PropTypes.bool,
};

export default PartnerBranchLinkModal;
